import type { PipeTransform } from '@angular/core';
import { Pipe } from '@angular/core';
import { TokenStorageService } from '../services/token-storage.service';

/**
 * Pipe that appends authentication token to URLs.
 * Used for image/video sources that can't use HTTP headers.
 *
 * Usage in template:
 * [src]="logoUrl | authenticatedUrl"
 * [src]="apiUrl + '/file/' + filename | authenticatedUrl"
 *
 * SECURITY NOTE: This pipe adds the JWT token to URLs as a query parameter.
 * The token will be visible in:
 * - Browser history
 * - Server logs
 * - Proxy logs
 *
 * Mitigation: Tokens have expiration (typically 1-24 hours).
 * For sensitive files, consider using the blob download approach instead.
 */
@Pipe({
  name: 'authenticatedUrl',
  pure: true
})
export class AuthenticatedUrlPipe implements PipeTransform {
  constructor(private readonly tokenStorage: TokenStorageService) { }

  transform(url: string | undefined | null): string {
    if (!url) {
      return '';
    }

    const token = this.tokenStorage.getToken();
    if (!token) {
      // No token available - log for debugging session issues
      console.warn('AuthenticatedUrlPipe: No token available for URL', url);
      return url;
    }

    // Check if token is expired before adding to URL
    if (this.isTokenExpired(token)) {
      console.warn('Token is expired, clearing and redirecting to login');
      this.tokenStorage.signOut();
      // Return URL without token - will fail with 401 and trigger auth redirect
      return url;
    }

    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}token=${encodeURIComponent(token)}`;
  }

  /**
   * Check if a JWT token is expired without validating the signature.
   * This is a client-side check to proactively avoid expired token requests.
   */
  private isTokenExpired(token: string): boolean {
    try {
      const payload = this.decodeJwtPayload(token);
      if (!payload || !payload['exp']) {
        return false; // No expiration claim, assume valid
      }

      // JWT exp is in seconds, Date.now() is in milliseconds
      const expirationTime = (payload['exp'] as number) * 1000;
      const currentTime = Date.now();

      // Add 30 second buffer to account for clock skew and network latency
      return currentTime >= (expirationTime - 30000);
    } catch (error) {
      // Log for debugging - token corruption or tampering
      console.warn('AuthenticatedUrlPipe: Failed to check token expiration', error);
      return false; // Let server validation handle the rejection
    }
  }

  /**
   * Decode JWT payload without validation (for client-side expiration check only)
   */
  private decodeJwtPayload(token: string): Record<string, unknown> | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.warn('AuthenticatedUrlPipe: Invalid JWT structure (expected 3 parts)');
        return null;
      }

      const payload = parts[1];
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decoded);
    } catch (error) {
      console.warn('AuthenticatedUrlPipe: Failed to decode JWT payload', error);
      return null;
    }
  }
}
